import Api from '@/Api'

export default class MemberApi extends Api {
  pendingMemberTransactions = (params) => {
    return this.get('/api/v1/private/members/transactions/drafts', params)
  }

  /**
   * Requests the API to send a list of members being edited/draft members
   * @param {Object}          params - key/value pair to be provided as url query parameters
   */
  requestDrafts = (params) => {
    return this.get('/api/v1/private/members/transactions/drafts')
  }

  /**
   * Given a User ID, a start date, and end date, and a transaction type, returns a history of user transactions
   * @param {Object}          [options={}] - configuration for the API request
   * @param {Object}          options.body - the request body
   * @param {String}          options.body.userId - the user's ID (i.e. their username)
   * @param {String}          options.body.startDate - start date for the transaction history
   * @param {String}          options.body.endDate - end date for the transaction history
   */
  transactionHistory = (options = {}) => {
    // console.log('transaction history', options);
    return this.post('/api/v1/private/members/transactions', options.body, options.params)
  }

  /**
   * Adds a new primary subscriber
   * @param {Object}          options - configuration for the API request
   * @param {String}          options.transactionId -
   * @param {String}          options.transactionType - "hold/add"
   * @param {String}          options.memberType - "Subscriber/Dependent"
   * @param {String}          options.subscriberCk - ""
   * @param {String}          options.subscriberMemberCk - ""
   * @param {String}          options.clientHealthId - ""
   * @param {String}          options.eyemedMemberId - ""
   * @param {String}          options.memberFirstName - ""
   * @param {String}          options.memberLastName - ""
   * @param {String}          options.memberMidInitial - ""
   * @param {String}          options.memberNameSuffix - ""
   * @param {String}          options.memberDateOfBirth - "02/24/1980"
   * @param {String}          options.ssn - ""
   * @param {String}          options.relationship - "M/S/D"
   * @param {String}          options.gender - ""
   * @param {String}          options.phone - ""
   * @param {String}          options.email - ""
   * @param {String}          options.memberIdSource - ""
   * @param {Object}          options.primaryAddress
   * @param {String}          options.primaryAddress.line1
   * @param {String}          options.primaryAddress.line2
   * @param {String}          options.primaryAddress.line3,
   * @param {String}          options.primaryAddress.city
   * @param {String}          options.primaryAddress.state
   * @param {String}          options.primaryAddress.zip
   * @param {String}          options.primaryAddress.county
   * @param {String}          options.primaryAddress.type
   * @param {Object}          options.coverage
   * @param {String}          options.coverage.groupCk
   * @param {String}          options.coverage.subGroupCk
   * @param {String}          options.coverage.classId
   * @param {String}          options.coverage.planId
   * @param {String}          options.coverage.startDate
   * @param {String}          options.coverage.endDate
   * @param {Object}          options.reportingCodes
   * @param {String}          options.reportingCodes.divisionCode
   * @param {String}          options.reportingCodes.benefitOption
   * @param {String}          options.reportingCodes.locationGroupNumber
   * @param {String}          options.reportingCodes.companyCode
   * @param {String}          options.reportingCodes.clientGroupNumber
   * @param {String[]}        options.reportingCodes.reportingCodes
   * @param {Object}          options.privacy
   * @param {String}          options.privacy.firstName
   * @param {String}          options.privacy.midInitial
   * @param {String}          options.privacy.lastName
   * @param {String}          options.privacy.address
   * @param {String}          options.privacy.address.line1
   * @param {String}          options.privacy.address.line2
   * @param {String}          options.privacy.address.line3,
   * @param {String}          options.privacy.address.city
   * @param {String}          options.privacy.address.state
   * @param {String}          options.privacy.address.zip
   * @param {String}          options.privacy.address.county
   * @param {Object}          options.ethnicity
   * @param {String}          options.ethnicity.code
   * @param {String}          options.ethnicity.description
   * @param {Object}          options.race
   * @param {String}          options.race.code
   * @param {String}          options.race.description
   * @param {Object}          options.spokenLanguage
   * @param {String}          options.spokenLanguage.code
   * @param {String}          options.spokenLanguage.description
   * @param {Object}          options.writtenLanguage
   * @param {String}          options.writtenLanguage.code
   * @param {String}          options.writtenLanguage.description
  }
  */
  addMember = (params = {}) => {
    try {
      // don't do validations for draft/hold submission
      if (params.transactionType !== 'Hold') {
        if (params.memberType === 'Dependent') {
          if (!params.subscriberCk) {
            throw new Error('Invalid param subscriberCk')
          }

          if (!params.subscriberMemberCk) {
            throw new Error('Invalid param subscriberMemberCk')
          }
        }

        if (!params.transactionType) {
          throw new Error('Invalid param transactionType')
        }

        if (!params.memberType) {
          throw new Error('Invalid param memberType (should be Dependent')
        }

        if (!params.coverage.groupCk) {
          throw new Error('Invalid param coverage.groupCk')
        }

        if (!params.coverage.subGroupCk) {
          throw new Error('Invalid param coverage.subGroupCk')
        }

        if (!params.coverage.classId) {
          throw new Error('Invalid param coverage.classId')
        }

        if (!params.coverage.planId) {
          throw new Error('Invalid param coverage.planId')
        }

        if (!params.coverage.startDate) {
          throw new Error('Invalid param coverage.startDate')
        }

        // check for undefined instead of !params.memberIdSource because null is a valid value
        if (params.memberIdSource === undefined) {
          if (process.env.NODE_ENV === 'development') {
            // remove the process.env.NODE_ENV !== 'development' once the API is fixed
            // console.log('memberIdSource was null for API call but is pending a patch to the Postman mock. Allowing to pass on dev environment')
          } else {
            throw new Error('Invalid param memberIdSource')
          }
        }

        if (params.memberIdSource === 'CS' && !params.clientHealthId) {
          throw new Error('Invalid param clientHealthId when memberIdSource = CS')
        }

        if (!params.memberFirstName) {
          throw new Error('Invalid param memberFirstName')
        }

        if (!params.memberLastName) {
          throw new Error('Invalid param memberLastName')
        }

        if (!params.memberDateOfBirth) {
          throw new Error('Invalid param memberDateOfBirth')
        }

        if (!params.relationship) {
          throw new Error('Invalid param relationship')
        }

        if (!params.gender) {
          throw new Error('Invalid param gender')
        }

        if (params.primaryAddress.locatedInUS === 'yes') {
          if (!params.primaryAddress.zip) {
            throw new Error('Invalid param zip for member located in US')
          }

          if (!params.primaryAddress.city) {
            throw new Error('Invalid param city for member located in US')
          }

          if (!params.primaryAddress.state) {
            throw new Error('Invalid param state for member located in US')
          }
        }

        if (!params.primaryAddress.line1) {
          throw new Error('Invalid param primaryAddress.line1')
        }

        if (params.selectedPlanInfo && params.selectedPlanInfo.subGroupInfo.divisionCodeSetting === 'Y' && !params.reportingCodes.divisionCode) {
          throw new Error('Invalid param reportingCodes.divisionCode')
        }

        if (params.selectedPlanInfo && params.selectedPlanInfo.subGroupInfo.benefitOptionSetting === 'Y' && !params.reportingCodes.benefitOption) {
          throw new Error('Invalid param reportingCodes.benefitOption')
        }

        if (params.selectedPlanInfo && params.selectedPlanInfo.subGroupInfo.locationCodeSetting === 'Y' && !params.reportingCodes.locationGroupNumber) {
          throw new Error('Invalid param reportingCodes.locationGroupNumber')
        }

        if (params.selectedPlanInfo && params.selectedPlanInfo.subGroupInfo.companyCodeSetting === 'Y' && !params.reportingCodes.companyCode) {
          throw new Error('Invalid param reportingCodes.companyCode')
        }
      }
    } catch (err) {
      return Promise.reject(err)
    }

    return this.post('/api/v1/private/members', params)
  }

  /**
   * Get member coverage history
   * @param {Object}          options - configuration for the API request
   * @param {Number}          options.memberCk - the member's CK
   * @param {Object}          options.params - query params
   */
  coverageHistory = (options = {}) => {
    return this.post(`/api/v1/private/members/coverages`, options.params)
  }

  /**
   * Adds a new primary subscriber
   * @param {Object}          options - configuration for the API request
   * @param {String}          options.transactionId -
   * @param {String}          options.transactionType - "hold/add"
   * @param {String}          options.memberType - "Subscriber/Dependent"
   * @param {String}          options.subscriberCk - ""
   * @param {String}          options.subscriberMemberCk - ""
   * @param {String}          options.clientHealthId - ""
   * @param {String}          options.eyemedMemberId - ""
   * @param {String}          options.memberFirstName - ""
   * @param {String}          options.memberLastName - ""
   * @param {String}          options.memberMidInitial - ""
   * @param {String}          options.memberNameSuffix - ""
   * @param {String}          options.memberDateOfBirth - "02/24/1980"
   * @param {String}          options.ssn - ""
   * @param {String}          options.relationship - "M/S/D"
   * @param {String}          options.gender - ""
   * @param {String}          options.phone - ""
   * @param {String}          options.email - ""
   * @param {String}          options.memberIdSource - ""
   * @param {Object}          options.primaryAddress
   * @param {String}          options.primaryAddress.line1
   * @param {String}          options.primaryAddress.line2
   * @param {String}          options.primaryAddress.line3,
   * @param {String}          options.primaryAddress.city
   * @param {String}          options.primaryAddress.state
   * @param {String}          options.primaryAddress.zip
   * @param {String}          options.primaryAddress.county
   * @param {String}          options.primaryAddress.type
   * @param {Object}          options.coverage
   * @param {String}          options.coverage.groupCk
   * @param {String}          options.coverage.subGroupCk
   * @param {String}          options.coverage.classId
   * @param {String}          options.coverage.planId
   * @param {String}          options.coverage.startDate
   * @param {String}          options.coverage.endDate
   * @param {Object}          options.reportingCodes
   * @param {String}          options.reportingCodes.divisionCode
   * @param {String}          options.reportingCodes.benefitOption
   * @param {String}          options.reportingCodes.locationGroupNumber
   * @param {String}          options.reportingCodes.companyCode
   * @param {String}          options.reportingCodes.clientGroupNumber
   * @param {String[]}        options.reportingCodes.reportingCodes
   * @param {Object}          options.privacy
   * @param {String}          options.privacy.firstName
   * @param {String}          options.privacy.midInitial
   * @param {String}          options.privacy.lastName
   * @param {String}          options.privacy.address
   * @param {String}          options.privacy.address.line1
   * @param {String}          options.privacy.address.line2
   * @param {String}          options.privacy.address.line3,
   * @param {String}          options.privacy.address.city
   * @param {String}          options.privacy.address.state
   * @param {String}          options.privacy.address.zip
   * @param {String}          options.privacy.address.county
   * @param {Object}          options.ethnicity
   * @param {String}          options.ethnicity.code
   * @param {String}          options.ethnicity.description
   * @param {Object}          options.race
   * @param {String}          options.race.code
   * @param {String}          options.race.description
   * @param {Object}          options.spokenLanguage
   * @param {String}          options.spokenLanguage.code
   * @param {String}          options.spokenLanguage.description
   * @param {Object}          options.writtenLanguage
   * @param {String}          options.writtenLanguage.code
   * @param {String}          options.writtenLanguage.description
  */
  updateMember = (options = {}) => {
    if (options.memberCk) {
      return this.put(`/api/v1/private/members`, options)
    } else {
      throw new Error('options.memberCk is undefined')
    }
  }

  /**
   * Gets member details from a particular memberCk
   * @param {Object}          options - configuration for the API request
   * @param {String}          options.action - action type. eg: "getMember"
   * @param {String}          options.memberCk
  */
  memberDetails = (options = {}) => {
    if (options.params.memberCk === undefined) { throw new Error('No memberCk provided, memberCk is required.') }
    return this.post(`/api/v1/private/members/detail`, options.params)
  }

  /**
   * Gets member details from a particular transactionId for pending members only
   * @param {Object}          options - configuration for the API request
   * @param {String}          options.transactionId - the id of the pending member transaction
   * @param {Object}          options.params - any query parameters you might need
   */
  getPendingMemberTransactionDetails = (options = {}) => {
    if (!options.transactionId) { throw new Error('No transactionId provided, transactionId is required.') }
    return this.get(`/api/v1/private/members/transactions/drafts/${options.transactionId}`, options.params)
  }

  getLocationDetailsFromZipCode = (options = {}) => {
    if (!options.zipCode) {
      throw new Error('No zip code provided')
    }

    return this.get(`/api/v1/private/zipcodes/${options.zipCode}`, options.params)
  }
}
